import { useState, useEffect, useMemo } from 'react'
import { useSearch } from 'features/search/context'

import { SisetuResult } from 'interface/sisetu'
import {
  CancerResult,
  useGetCancerResultBySisetu,
  useGetCancerDisease
} from 'interface/cancer'

import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'

import { CancerResultViewType } from './types'
import { CancerResultGraph } from './graph'
import { CancerResultTable } from './table'

import {
  Stack,
  StackProps,
  Box,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  SelectProps,
  RadioGroupProps,
} from '@mui/material'
import { useSisetuPreview } from 'features/sisetu/preview/container'

interface Disease {
  code: string
  name: string
}
const DiseaseSelect = (
  props: {
    diseaseList: Disease[]
  } & SelectProps<string>
) => {
  const { diseaseList, ...others } = props
  return (
    <FormControl>
      <Select size="small" sx={{ width: '15em' }} {...others}>
        {diseaseList.map((disease) => (
          <MenuItem key={`disease_${disease.code}`} value={disease.code}>
            {disease.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

interface StageClass {
  code: string
  name: string
}
const StageClassChoice = (
  props: {
    stageClassList: StageClass[]
  } & RadioGroupProps
) => {
  const { stageClassList, ...others } = props
  const stageClassItem = (stageClass: StageClass) => (
    <FormControlLabel
      key={`stage-class-${stageClass.code}`}
      value={stageClass.code}
      control={<Radio />}
      label={stageClass.name}
    />
  )
  return (
    <FormControl>
      <RadioGroup row {...others}>
        {stageClassList.map((stageClass) => stageClassItem(stageClass))}
      </RadioGroup>
    </FormControl>
  )
}
const ViewTypeChoice = (props: RadioGroupProps) => {
  return (
    <FormControl>
      <RadioGroup row {...props}>
        <FormControlLabel
          key={'view-type-stage'}
          value={CancerResultViewType.Stage}
          control={<Radio />}
          label={'病期別'}
        />
        <FormControlLabel
          key={'view-type-ages'}
          value={CancerResultViewType.AgeAndGenders}
          control={<Radio />}
          label={'患者属性別'}
        />
      </RadioGroup>
    </FormControl>
  )
}

type SisetuPreviewCancerPageProps = {
  sisetu: SisetuResult,
  container: ReturnType<typeof useSisetuPreview>
} & StackProps
export const SisetuPreviewCancerPage = ({
  sisetu,
  //container,
  ...others
}: SisetuPreviewCancerPageProps) => {
  const search = useSearch()

  const [diseaseList, setDiseaseList] = useState<Disease[]>([])
  const [selectedDiseaseCode, setSelectedDiseaseCode] = useState<string | null>(null)

  const [stageClassList, setStageClassList] = useState<StageClass[]>([])
  const [selectedStageClassCode, setSelectedStageClassCode] = useState<string | null>(null)

  const [viewType, setViewType] = useState<number>(CancerResultViewType.Stage)
  const [filteredList, setFilteredList] = useState<CancerResult[] | null>(null)

  const defaultDiseaseCode = useMemo(
    () => {
      const cancerCodeList =
        search?.disease
          ?.map((d) => d.cancerCode)
          ?.filter((code): code is NonNullable<typeof code> => code !== null) ||
        []
      return cancerCodeList.length > 0 ? cancerCodeList[0] : undefined
    },
    [search]
  )
  const resultState = useGetCancerResultBySisetu(sisetu.sisetuCode)
  useEffect(
    () => {
      const diseaseMap: { [index: string]: Disease } = {}
      const stageClassMap: { [index: string]: StageClass } = {}
      for (const record of resultState.data?.list || []) {
        if (!diseaseMap[record.diseaseCode]) {
          diseaseMap[record.diseaseCode] = {
            code: record.diseaseCode,
            name: record.diseaseName
          } as Disease
        }
        if (!stageClassMap[record.stageClassCode]) {
          stageClassMap[record.stageClassCode] = {
            code: record.stageClassCode,
            name: record.stageClassName
          } as StageClass
        }
      }
      setDiseaseList(Object.keys(diseaseMap).map((code) => diseaseMap[code]))
      setStageClassList(Object.keys(stageClassMap).map((code) => stageClassMap[code]))

      let nextDiseaseCode: string | null = null
      if (selectedDiseaseCode && diseaseMap[selectedDiseaseCode]) {
        nextDiseaseCode = selectedDiseaseCode
      } else if (search && defaultDiseaseCode && diseaseMap[defaultDiseaseCode]) {
        nextDiseaseCode = defaultDiseaseCode
      }
      if (!nextDiseaseCode) {
        nextDiseaseCode = resultState.data?.list[0]?.diseaseCode ?? null
      }
      setSelectedDiseaseCode(nextDiseaseCode)
      setFilteredList(null)
    },
    [resultState.data, defaultDiseaseCode, search, selectedDiseaseCode]
  )
  const diseaseState = useGetCancerDisease(selectedDiseaseCode)
  useEffect(
    () => {
      if (selectedStageClassCode) return
      if (diseaseState.data) {
        setSelectedStageClassCode(
          diseaseState.data.stageClassCode ?? resultState.data?.list[0]?.diseaseCode ?? null
        )  
      } else {
        setSelectedStageClassCode(null)
      }
    },
    [diseaseState.data, resultState.data, selectedStageClassCode]
  )
  useEffect(
    () => {
      setFilteredList(
        resultState.data?.list.filter(
          (r) => r.diseaseCode === selectedDiseaseCode && r.stageClassCode === selectedStageClassCode
        ) || null
      )
    },
    [resultState.data, selectedDiseaseCode, selectedStageClassCode]
  )
  return (
    <Stack
      direction="column"
      sx={{
        maxHeight: '100%',
        height: '100%',
      }}
      {...others}
    >
      {(resultState.loading || diseaseState.loading) && <Loading />}
      {resultState.error && <ErrorInfo error={resultState.error} padding={1} />}
      {diseaseState.error && (
        <ErrorInfo error={diseaseState.error} padding={1} />
      )}

      {resultState.data && diseaseState.data ? (
        <Stack direction="row" spacing={2} padding={1}>
          <DiseaseSelect
            name="disease-choice"
            diseaseList={diseaseList}
            defaultValue={defaultDiseaseCode ?? diseaseList[0]?.code ?? ''}
            value={selectedDiseaseCode ?? undefined}
            onChange={(event) => setSelectedDiseaseCode(event.target.value)}
            variant="outlined"
            size="small"
          />
          <StageClassChoice
            name="stage-class-choice"
            stageClassList={stageClassList}
            defaultValue={diseaseState.data.stageClassCode}
            value={selectedStageClassCode}
            onChange={(_, value) => setSelectedStageClassCode(value)}
          />
          <Box sx={{ flexGrow: 1 }} />
          <ViewTypeChoice
            name="view-type-choice"
            defaultValue={'0'}
            onChange={(_, value) => setViewType(parseInt(value))}
          />
        </Stack>
      ) : (
        <></>
      )}

      {resultState.data &&
      diseaseState.data &&
      selectedDiseaseCode &&
      selectedStageClassCode ? (
        <>
          {filteredList && (
            <CancerResultGraph
              list={filteredList}
              disease={diseaseState.data}
              viewType={viewType}
              style={{
                flexGrow: 1,
              }}
            />
          )}

          {filteredList && (
            <CancerResultTable
              list={filteredList}
              disease={diseaseState.data}
              viewType={viewType}
            />
          )}
        </>
      ) : (
        <></>
      )}
    </Stack>
  )
}
